import React, { useState } from 'react';
import { Project, Route } from '../../types';
import { useModalClose } from '../../hooks/useModalClose';
import { URLManager } from '../../lib/url/URLManager';
import { closeMenuAndUpdateUrl } from '../../lib/url/menuUtils';

interface RoutesMenuProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (route: Route, updates: Partial<Route>) => void;
}

export const RoutesMenu: React.FC<RoutesMenuProps> = ({
  project,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const urlManager = React.useRef(URLManager.getInstance());
  const modalRef = useModalClose({ 
    onClose: () => {
      closeMenuAndUpdateUrl(project.identifier);
      onClose();
    }, 
    project,
    view: 'routes',
    onSave: async () => {
      // Save any pending changes in all route cards
      const routeCards = document.querySelectorAll('.route-card');
      for (const card of routeCards) {
        const routeId = card.getAttribute('data-route-id');
        if (routeId) {
          const route = project.network.routes.find(r => r.id === routeId);
          if (route) {
            const form = card.querySelector('form');
            if (form) {
              const formData = new FormData(form);
              const updates: Partial<Route> = {};
              
              // Check for changes in form fields
              if (formData.get('distance') !== route.distance?.toString()) {
                updates.distance = parseFloat(formData.get('distance') as string);
              }
              if (formData.get('distanceCalculation') !== route.distanceCalculation) {
                updates.distanceCalculation = formData.get('distanceCalculation') as 'manual' | 'automatic';
              }
              if (formData.get('landType') !== route.landType) {
                updates.landType = formData.get('landType') as 'domestic' | 'customs' | 'no-customs';
              }
              
              if (Object.keys(updates).length > 0) {
                await onUpdate(route, updates);
              }
            }
          }
        }
      }
    }
  });

  // Update URL when menu opens
  React.useEffect(() => {
    if (isOpen) {
      urlManager.current.updateURL(
        project.identifier,
        'network',
        'routes',
        'auto',
        'routes_menu_open'
      );
    }
  }, [isOpen, project.identifier]);

  const handleDistanceCalculationChange = (route: Route, mode: 'manual' | 'automatic') => {
    onUpdate(route, { distanceCalculation: mode });
  };

  // Filter routes based on search term
  const filteredRoutes = project.network.routes.filter(route => {
    const sourceStation = project.network.stations.find(s => s.id === route.source);
    const targetStation = project.network.stations.find(s => s.id === route.target);
    if (!sourceStation || !targetStation) return false;
    
    const routeName = `${sourceStation.name} ${targetStation.name}`.toLowerCase();
    return routeName.includes(searchTerm.toLowerCase());
  });

  return (
    <div className={`routes-menu ${isOpen ? 'open' : ''}`}>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-2">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold">Routes</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search and Global Actions */}
          <div className="p-4 border-b">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input
                type="text"
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border rounded"
              />
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    filteredRoutes.forEach(route => {
                      onUpdate(route, { distanceCalculation: 'manual' });
                    });
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium w-full sm:w-[180px] flex flex-col items-center"
                >
                  <span>Set all to manual</span>
                  <span>({Math.ceil(filteredRoutes.reduce((sum, route) => sum + route.distance, 0)).toLocaleString()} km)</span>
                </button>
                <button
                  onClick={() => {
                    filteredRoutes.forEach(route => {
                      onUpdate(route, { distanceCalculation: 'automatic' });
                    });
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium w-full sm:w-[180px] flex flex-col items-center"
                >
                  <span>Set all to automatic</span>
                  <span>({Math.ceil(filteredRoutes.reduce((sum, route) => sum + (route.automaticDistance || 0), 0)).toLocaleString()} km)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Route List */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid gap-4">
              {filteredRoutes.map(route => {
                const sourceStation = project.network.stations.find(s => s.id === route.source);
                const targetStation = project.network.stations.find(s => s.id === route.target);
                
                if (!sourceStation || !targetStation) return null;

                return (
                  <div
                    key={route.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 route-card"
                    data-route-id={route.id}
                  >
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      // Save changes when form is submitted
                      const updates: Partial<Route> = {};
                      if (route.distance !== route.distance) {
                        updates.distance = route.distance;
                      }
                      if (route.distanceCalculation !== route.distanceCalculation) {
                        updates.distanceCalculation = route.distanceCalculation;
                      }
                      if (route.landType !== route.landType) {
                        updates.landType = route.landType;
                      }
                      if (Object.keys(updates).length > 0) {
                        onUpdate(route, updates);
                      }
                    }}>
                      <div className="mb-2 font-medium">
                        {sourceStation.name} ↔ {targetStation.name}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                          <input
                            type="number"
                            value={route.distance}
                            onChange={(e) => onUpdate(route, { distance: Number(e.target.value) })}
                            onBlur={() => {
                              const form = document.querySelector(`.route-card[data-route-id="${route.id}"] form`);
                              if (form) {
                                const formData = new FormData(form as HTMLFormElement);
                                const updates: Partial<Route> = {};
                                
                                if (formData.get('distance') !== route.distance?.toString()) {
                                  updates.distance = parseFloat(formData.get('distance') as string);
                                }
                                if (formData.get('landType') !== route.landType) {
                                  updates.landType = formData.get('landType') as 'domestic' | 'customs' | 'no-customs';
                                }
                                
                                if (Object.keys(updates).length > 0) {
                                  onUpdate(route, updates);
                                }
                              }
                            }}
                            className="w-full px-3 py-1.5 border rounded-md text-sm"
                            placeholder="Distance in km"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="relative w-full">
                            <select
                              value={route.landType}
                              onChange={(e) => onUpdate(route, { landType: e.target.value as 'domestic' | 'customs' | 'no-customs' })}
                              onBlur={() => {
                                const form = document.querySelector(`.route-card[data-route-id="${route.id}"] form`);
                                if (form) {
                                  const formData = new FormData(form as HTMLFormElement);
                                  const updates: Partial<Route> = {};
                                  
                                  if (formData.get('distance') !== route.distance?.toString()) {
                                    updates.distance = parseFloat(formData.get('distance') as string);
                                  }
                                  if (formData.get('landType') !== route.landType) {
                                    updates.landType = formData.get('landType') as 'domestic' | 'customs' | 'no-customs';
                                  }
                                  
                                  if (Object.keys(updates).length > 0) {
                                    onUpdate(route, updates);
                                  }
                                }
                              }}
                              className="px-3 py-1.5 rounded-md text-sm w-full bg-white border border-gray-200 appearance-none pr-8"
                              style={{
                                WebkitAppearance: 'none',
                                MozAppearance: 'none'
                              }}
                            >
                              <option value="domestic">No border crossing</option>
                              <option value="no-customs">Border without customs</option>
                              <option value="customs">Border with customs</option>
                            </select>
                            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                              <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleDistanceCalculationChange(route, 'manual')}
                            className={`px-3 py-1.5 rounded-md text-sm min-w-[160px] ${
                              route.distanceCalculation === 'manual'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                          >
                            Manual ({Math.ceil(route.distance).toLocaleString()} km)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDistanceCalculationChange(route, 'automatic')}
                            className={`px-3 py-1.5 rounded-md text-sm min-w-[160px] ${
                              route.distanceCalculation === 'automatic'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                          >
                            Automatic {route.automaticDistance ? `(${Math.ceil(route.automaticDistance).toLocaleString()} km)` : '(N/A)'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 